import React, { useState, useEffect } from "react";
import DetailPlanView from "./DetailPlanView";
// import OthersCommentView from "./OthersCommentView";
import { 
  Grid, TextField, Button, MenuItem, Select, FormControl, 
  InputLabel, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Box, Alert, CircularProgress,
  Paper, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from "@mui/material";
import { Download, Visibility, Close, PictureAsPdf, Print, Comment } from "@mui/icons-material";

import FormContainer from "../../../../../components/Forms/FormContainer2";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests";
import { IPDCToastMessageResult } from "../../../../../components/Controls";
import config from "../../../../../config";

const RegisterComment = () => {
  const decodedUser = useDecodedUser();
  // Get token from localStorage or your auth mechanism
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  const [requestNumber, setRequestNumber] = useState("");
  const [requestNumbers, setRequestNumbers] = useState([]);
  const [commentType, setCommentType] = useState("");
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: "info", text: "" });
  const [toastMessage, setToastMessage] = useState({ message: "", type: "" });
  const [showDetail, setShowDetail] = useState(false);
  const [showOthersComment, setShowOthersComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [othersComments, setOthersComments] = useState([]);
  const [loadingOthersComments, setLoadingOthersComments] = useState(false);
  
  // State for modal and file info
  const [modalOpen, setModalOpen] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [fetchingFile, setFetchingFile] = useState(false);
  const [buildingPermitDetails, setBuildingPermitDetails] = useState(null);
  const [availableFiles, setAvailableFiles] = useState([]);

  useEffect(() => {
    // Fetch request numbers from the API
    GetDataFromApiWithParams("buildingPermits")
      .then((responseData) => {
        setRequestNumbers(responseData);
        console.log("Request numbers:", responseData);
      })
      .catch((error) => {
        console.error("Error fetching request numbers", error);
        setToastMessage({ message: "Error fetching request numbers", type: "error" });
      });
  }, []);

  // Fetch building permit details when request number changes
  useEffect(() => {
    const fetchBuildingPermitDetails = async () => {
      if (requestNumber) {
        try {
          setFetchingFile(true);
          // Try different endpoints to get the details
          let response;
          try {
            // First try with id
            response = await GetDataFromApiWithParams(`buildingPermits/${requestNumber}/details`);
          } catch (error) {
            // If that fails, try with the regular endpoint
            response = await GetDataFromApiWithParams(`buildingPermits/${requestNumber}`);
          }
          
          if (response) {
            console.log("Building permit details:", response);
            
            // Handle both array and single object responses
            const details = Array.isArray(response) ? response[0] : response;
            console.log("Building permit details:", details)
            setBuildingPermitDetails(details);
            
            // Check available files (only PDF files)
            const files = [];
            
            // Helper function to check if file is PDF
            const isPDF = (path) => {
              return path && path.toLowerCase().endsWith('.pdf');
            };
            
            if (details.plan_archtectural_path && isPDF(details.plan_archtectural_path)) {
              files.push({ type: 'architectural', path: details.plan_archtectural_path });
            }
            if (details.plan_structural_path && isPDF(details.plan_structural_path)) {
              files.push({ type: 'structural', path: details.plan_structural_path });
            }
            if (details.plan_sanitary_path && isPDF(details.plan_sanitary_path)) {
              files.push({ type: 'sanitary', path: details.plan_sanitary_path });
            }
            if (details.plan_electrical_path && isPDF(details.plan_electrical_path)) {
              files.push({ type: 'electrical', path: details.plan_electrical_path });
            }
            if (details.plan_electro_mechanical_path && isPDF(details.plan_electro_mechanical_path)) {
              files.push({ type: 'electro-mechanical', path: details.plan_electro_mechanical_path });
            }
            if (details.plan_environmental_path && isPDF(details.plan_environmental_path)) {
              files.push({ type: 'environmental', path: details.plan_environmental_path });
            }
            if (details.plan_other_path && isPDF(details.plan_other_path)) {
              files.push({ type: 'other', path: details.plan_other_path });
            }
            
            setAvailableFiles(files);
            console.log("Available PDF files:", files);
          }
        } catch (error) {
          console.error("Error fetching building permit details:", error);
          setMessage({ type: "error", text: "Error fetching building permit details" });
          setToastMessage({ message: "Error fetching building permit details", type: "error" });
        } finally {
          setFetchingFile(false);
        }
      }
    };

    fetchBuildingPermitDetails();
  }, [requestNumber]);

  useEffect(() => {
    const fetchOthersComments = async () => {
      if (requestNumber) {
        try {
         
          setLoadingOthersComments(true);
          console.log("Fetching comments for permit ID:", requestNumber);
          
          // Based on your queries, you have getRequestedBuildingPermitCommentById
          // which filters by permit_no
          const response = await GetDataFromApiWithParams(`buildingPermits/comment/${requestNumber}`);
          
          console.log("Comments response:", response);
          
          if (response) {
            // Handle different response formats
            let comments = [];
            
            if (Array.isArray(response)) {
              comments = response;
            } else if (response.data && Array.isArray(response.data)) {
              comments = response.data;
            } else if (response.data) {
              comments = [response.data];
            } else if (response.comments) {
              comments = Array.isArray(response.comments) ? response.comments : [response.comments];
            } else {
              comments = [response];
            }
            
            // Filter out any null values
            comments = comments.filter(c => c !== null);
            setOthersComments(comments);
          }
        } catch (error) {
          console.error("Error fetching others comments:", error);
          console.error("Error details:", {
            message: error.message,
            response: error.response,
            status: error.response?.status,
            data: error.response?.data
          });
          setToastMessage({ 
            message: error.response?.data?.message || "Error fetching others comments", 
            type: "error" 
          });
          setOthersComments([]); // Reset to empty array on error
        } finally {
          setLoadingOthersComments(false);
        }
      }
    };
  
    if (showOthersComment) {
      fetchOthersComments();
    }
  }, [requestNumber, showOthersComment]);

  const getFilePath = () => {
    if (!buildingPermitDetails || !commentType) return null;

    // Map comment type to database column names
    const filePathMap = {
      architectural: buildingPermitDetails.plan_archtectural_path,
      sanitary: buildingPermitDetails.plan_sanitary_path,
      structural: buildingPermitDetails.plan_structural_path,
      "electro-mechanical": buildingPermitDetails.plan_electro_mechanical_path,
      electrical: buildingPermitDetails.plan_electrical_path,
      environmental: buildingPermitDetails.plan_environmental_path,
      other: buildingPermitDetails.plan_other_path,
    };

    let filePath = filePathMap[commentType];
    
    // Check if the path is valid and is a PDF
    if (!filePath || !filePath.toLowerCase().endsWith('.pdf')) {
      console.log(`Invalid or non-PDF file for ${commentType}:`, filePath);
      return null;
    }
    
    // Remove any duplicate 'uploads/' prefix for public URL
    if (filePath.startsWith('uploads/')) {
      filePath = filePath.substring(8); // Remove 'uploads/' prefix
    }
    if (filePath.startsWith('/uploads/')) {
      filePath = filePath.substring(9); // Remove '/uploads/' prefix
    }
    
    return filePath;
  };

  const handleDownloadPlan = async () => {
    try {
      if (!commentType || !requestNumber) {
        setMessage({ type: "warning", text: "Please select both request number and comment type" });
        setToastMessage({ message: "Please select both request number and comment type", type: "warning" });
        return;
      }

      const filePath = getFilePath();
      
      if (!filePath) {
        // Show available files for this request
        const availableTypes = availableFiles.map(f => f.type).join(', ');
        setMessage({ 
          type: "warning", 
          text: `No PDF file found for "${commentType}". Available PDF files: ${availableTypes || 'none'}`
        });
        setToastMessage({ 
          message: `No PDF file found for "${commentType}"`, 
          type: "warning" 
        });
        return;
      }

      console.log("Downloading file:", filePath);
      
      // Construct full URL with uploads prefix for authenticated download
      const fullUrl = `${config.apiUrl}/uploads/${filePath}`;
      console.log("Download URL:", fullUrl);
      
      // Use fetch with authentication headers
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication failed. Please log in again.");
        }
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filePath.split('/').pop());
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: "success", text: "Download started" });
      setToastMessage({ message: "Download started", type: "success" });
    } catch (error) {
      console.error("Download error:", error);
      setMessage({ type: "error", text: error.message || "Error downloading file" });
      setToastMessage({ message: error.message || "Error downloading file", type: "error" });
    }
  };

  const handleViewDetailPlan = () => {
    if (!requestNumber) {
      setMessage({ type: "warning", text: "Please select a request number first" });
      setToastMessage({ message: "Please select a request number first", type: "warning" });
      return;
    }
    setShowDetail(!showDetail);
    // Hide others comment when showing detail
    if (showOthersComment) setShowOthersComment(false);
  };

  const handleViewOthersComment = () => {
    if (!requestNumber) {
      setMessage({ type: "warning", text: "Please select a request number first" });
      setToastMessage({ message: "Please select a request number first", type: "warning" });
      return;
    }
    setShowOthersComment(!showOthersComment);
    // Hide detail when showing others comment
    if (showDetail) setShowDetail(false);
  };

  const handleViewPDF = async () => {
    try {
      if (!commentType || !requestNumber) {
        setMessage({ type: "warning", text: "Please select both request number and comment type" });
        setToastMessage({ message: "Please select both request number and comment type", type: "warning" });
        return;
      }

      const filePath = getFilePath();
      
      if (!filePath) {
        const availableTypes = availableFiles.map(f => f.type).join(', ');
        setMessage({ 
          type: "warning", 
          text: `No PDF file found for "${commentType}". Available PDF files: ${availableTypes || 'none'}`
        });
        setToastMessage({ 
          message: `No PDF file found for "${commentType}"`, 
          type: "warning" 
        });
        return;
      }

      setFetchingFile(true);
      
      // Fetch the file with authentication headers
      const fullUrl = `${config.apiUrl}/uploads/${filePath}`;
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      // Get file info
      const fileName = filePath.split('/').pop();
      
      setFileInfo({
        name: fileName,
        blobUrl: blobUrl,
      });
      
      setModalOpen(true);
      setFetchingFile(false);
    } catch (error) {
      console.error("Error viewing PDF:", error);
      setMessage({ type: "error", text: error.message || "Error accessing file" });
      setToastMessage({ message: error.message || "Error accessing file", type: "error" });
      setFetchingFile(false);
    }
  };

  const handleCloseModal = () => {
    // Clean up blob URL when closing modal
    if (fileInfo?.blobUrl) {
      URL.revokeObjectURL(fileInfo.blobUrl);
    }
    setModalOpen(false);
    setFileInfo(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSubmitComment = async () => {
    if (!comment.trim()) {
      setMessage({ type: "warning", text: "Please enter a comment" });
      setToastMessage({ message: "Please enter a comment", type: "warning" });
      return;
    }
    
    setLoading(true);
    try {
      
      const responseData = await ExecutePostWithParams(`buildingPermits/${requestNumber}/comment`, {
        requestNumber,
        commentedBy: decodedUser.full_name,
        commentType,
        comment,
      });
      setMessage({ type: "success", text: responseData.message || "Comment submitted successfully" });
      setToastMessage({ message: responseData.message || "Comment submitted successfully", type: "success" });
      setComment("");
      
      // Refresh others comments if they are being shown
      
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Error submitting comment" });
      setToastMessage({ message: error.message || "Error submitting comment", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const renderPDFViewer = () => {
    if (!fileInfo) return null;

    return (
      <iframe
        src={fileInfo.blobUrl}
        width="100%"
        height="100%"
        style={{ 
          border: 'none',
          minHeight: '600px'
        }}
        title="View Plan"
      />
    );
  };

  const renderOthersCommentsTable = () => {
    if (loadingOthersComments) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }
  
    if (!othersComments || othersComments.length === 0) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No comments found for this request.
          </Typography>
        </Paper>
      );
    }
  
    // Filter out any null items and ensure we have valid data
    const validComments = othersComments.filter(comment => comment !== null);
  
    if (validComments.length === 0) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No valid comments found.
          </Typography>
        </Paper>
      );
    }
  
    return (
      
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        {console.log("Commentttttt", validComments)}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell  sx={{ color: '#000000', fontWeight: 600, fontFamily: '"Times New Roman", Times, serif'}}>
                Comment Type</TableCell>
                <TableCell  sx={{ color: '#000000', fontWeight: 600, fontFamily: '"Times New Roman", Times, serif'}}>
                  Comment</TableCell>
                  <TableCell  sx={{ color: '#000000', fontWeight: 600, fontFamily: '"Times New Roman", Times, serif'}}>
                    Commented By</TableCell>
                    <TableCell  sx={{ color: '#000000', fontWeight: 600, fontFamily: '"Times New Roman", Times, serif'}}>
                      Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validComments.map((comment, index) => (
              <TableRow key={comment?.id || index} hover>
                <TableCell>{comment?.comment_type || 'N/A'}</TableCell>
                <TableCell>{comment?.comment || 'N/A'}</TableCell>
                <TableCell>
                  {comment?.commented_by_name || comment?.commented_by || 'N/A'}
                </TableCell>
                <TableCell>
                  {comment?.date_of_comment 
                    ? new Date(comment.date_of_comment).toLocaleDateString() 
                    : 'N/A'
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <FormContainer title="Professional Comment Submition" onSubmit={(e) => e.preventDefault()}>
      <Grid item xs={12}>
        <FormControl fullWidth={true}>
          <InputLabel>Request Number</InputLabel>
          <Select
            value={requestNumber}
            onChange={(e) => {
              setRequestNumber(e.target.value);
              setCommentType(""); // Reset comment type when request changes
              setShowDetail(false);
              setShowOthersComment(false);
            }}
            error={!!errors.requestNumber}
          >
            {requestNumbers.map((num) => (
              <MenuItem key={num.id} value={num.id}>
                Building Permit Id:- {num.id} - Agreement Id:- {num.agreement_id} (Company Name:- {num.company})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={9} md={9}>
        <FormControl fullWidth={true}>
          <InputLabel>Comment Type</InputLabel>
          <Select 
            value={commentType} 
            onChange={(e) => setCommentType(e.target.value)} 
            error={!!errors.commentType}
          >
            <MenuItem value="architectural">Architectural</MenuItem>
            <MenuItem value="sanitary">Sanitary</MenuItem>
            <MenuItem value="structural">Structural</MenuItem>
            <MenuItem value="electro-mechanical">Electro-Mechanical</MenuItem>
            <MenuItem value="electrical">Electrical</MenuItem>
            <MenuItem value="environmental">Environmental</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={3} md={3}>
      <Button 
          startIcon={<Download />} 
          onClick={handleDownloadPlan}
          disabled={!requestNumber || !commentType || fetchingFile}
          variant="contained"
          fullWidth
          sx={{ 
            backgroundColor: '#e0e0e0',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#999999',
            }
          }}
        >
          {fetchingFile ? <CircularProgress size={24} /> : "Download"}
        </Button>
      </Grid>

      {/* Available Files Info */}
      {buildingPermitDetails && availableFiles.length > 0 && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Available PDF Files for Request #{requestNumber}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableFiles.map((file) => (
                  <Chip
                    key={file.type}
                    label={file.type}
                    size="small"
                    
                    variant={file.type === commentType ? "filled" : "outlined"}
                    onClick={() => setCommentType(file.type)}
                    icon={<PictureAsPdf />}
                    sx={{
                      backgroundColor: '#D0EBD0',
                      '&:hover': {
                        backgroundColor: '#C0DBC0',
                      },
                      ...(file.type === commentType && {
                        backgroundColor: '#4caf50',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#45a049',
                        },
                      }),
                    }}
                  />
                ))}
              </Box>
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            onClick={handleViewDetailPlan}
            disabled={!requestNumber}
            startIcon={<Visibility />}
           
          >
            {showDetail ? "HIDE PERMIT DETAIL" : "VIEW PERMIT DETAIL"}
          </Button>
          
          <Button 
            variant="outlined" 
            onClick={handleViewOthersComment}
            disabled={!requestNumber}
            startIcon={<Comment />}
           
          >
            {showOthersComment ? "HIDE OTHERS COMMENT" : "VIEW OTHERS COMMENT"}
          </Button>
          
          {buildingPermitDetails && commentType && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
              {commentType} - {buildingPermitDetails.agreement_id}
            </Typography>
          )}
          
          <Button 
            variant="contained" 
            onClick={handleViewPDF}
            disabled={!requestNumber || !commentType || fetchingFile}
            startIcon={<PictureAsPdf />}
            sx={{ 
              backgroundColor: '#e0e0e0',
              color: '#000000',
              '&:hover': {
                backgroundColor: '#d5d5d5',
              },
              '&.Mui-disabled': {
                backgroundColor: '#f5f5f5',
                color: '#999999',
              },
              ml: 'auto'
            }}
          >
            {fetchingFile ? <CircularProgress size={24} /> : "VIEW Plan"}
          </Button>
        </Box>
      </Grid>

      {showDetail && (
        <Grid item xs={12}>
          <DetailPlanView permitRequestNumber={requestNumber} />
        </Grid>
      )}

      {showOthersComment && (
      <Grid item xs={12}>
  <Box sx={{ mt: 2 }}>
    <Typography 
      variant="body2" 
      gutterBottom
      sx={{ 
        color: '#000000',
        fontWeight: 600,
        fontFamily: '"Times New Roman", Times, serif'
      }}
    >
      Comments from Other Experts
    </Typography>
    {renderOthersCommentsTable()}
  </Box>
</Grid>
      )}

      {/* Comment box and submit button */}
      <Grid item xs={12}>
        <TextField
          label="Add your comment"
          multiline
          rows={4}
          fullWidth={true}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          error={!!errors.comment}
          helperText={errors.comment}
        />
      </Grid>

      <Grid item xs={12}>
      <Button
          variant="contained"
          onClick={handleSubmitComment}
          disabled={loading || !requestNumber || !commentType || !comment.trim()}
          sx={{ 
            mt: 1,
            backgroundColor: '#e0e0e0',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#d5d5d5',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              color: '#999999',
            }
          }}
        >
          {loading ? <CircularProgress size={24} /> : "SUBMIT COMMENT"}
        </Button>
      </Grid>

      {/* Alert Message */}
      {/* {message.text && (
        <Grid item xs={12}>
          <Alert 
            severity={message.type || "info"}
            onClose={() => setMessage({ type: "info", text: "" })}
          >
            {message.text}
          </Alert>
        </Grid>
      )} */}

      {/* Toast Message */}
      {toastMessage.message && (
        <Grid item xs={12}>
          <IPDCToastMessageResult 
            message={toastMessage.message} 
            type={toastMessage.type} 
          />
        </Grid>
      )}

      {/* PDF Preview Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PictureAsPdf color="error" />
              <Typography variant="h6">
                PDF Preview: {fileInfo?.name}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: 'calc(100% - 80px)' }}>
          {fetchingFile ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : (
            renderPDFViewer()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
          <Button 
            onClick={handleDownloadPlan} 
            variant="contained" 
            startIcon={<Download />}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </FormContainer>
    
  );
};

export default RegisterComment;